import { SortDirectionType, SortFieldDisplayProps, SortingOptionsType } from '@components/design/migrated/Table';
import { SortDirection } from '@components/design/migrated/Table/Sorting/types';
import { CollectionLiveData } from '@components/collections/Model';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { PerformanceType } from '@const/PerformanceType';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { Filters, Option, ShareType } from '@utils/pageConfig/Types';

export interface PerformanceAnalysisboardPgPage {
  content: PerformanceAnalysisboardPg[];
  pageable: Pageable;
}

export interface PerformanceAnalysisboardPg {
  segmentOrPg: string;
  country?: Country;
  marketRevenue: Kpi;
  marketVolume: Kpi;
  yourRevenue: Kpi;
  yourVolume: Kpi;
  marketPrice: Kpi;
  yourPrice: Kpi;
  marketExcludeRetailRevenue: Kpi;
  marketExcludeRetailVolume: Kpi;
  marketExcludeRetailPrice: Kpi;
  yourRevenueShare: KpiPercentage;
  yourVolumeShare: KpiPercentage;
  yourRevenueMix: KpiPercentage;
  yourVolumeMix: KpiPercentage;
}

export interface PageRequest {
  page: number;
  size: number;
}

export interface PerformanceAnalysisboardReqParam extends CollectionLiveData {
  clientId: number;
  productType: ProductType;
  countryIds: string[];
  productGroupForPA: Option[] | undefined;
  period: string;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  periodTypeForPA: Periodicity;
  shareType: ShareType;
  appliedFilters: Filters[];
  segment: string;
  dataType: string;
  kpi?: string;
  segmentBy?: string;
  segmentByDataType?: string;
}

export interface PerformanceAnalysisboardReq extends CollectionLiveData {
  clientId: number;
  productType: ProductType;
  productDataType: ProductDataType;
  productGroup: string[];
  country: string[];
  period: string;
  comparisonPeriod: string;
  periodType: string;
  priceBand: string[];
  shareType: string;
  showPerformance: string;
  dataType: string;
  split: string[];
  channel: string[];
  region: string[];
  city: string[];
  brand: string[];
  segmentFeature: string[];
  feature: string;
  featureValue: string[];
  kpi?: string;
  segmentBy?: string;
  segmentByDataType?: string;
  targetCurrency?: string;
}

interface Pageable {
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPage: number;
}

interface Country {
  countryCode: string;
  countryName: string;
  currency: Currency;
}

interface Currency {
  currencyCode: string;
  currencyName: string;
  stCurrencyId: number;
}

export interface Kpi {
  value: number;
  percentage: number;
  actual: number;
}

export interface KpiPercentage {
  growthPercentage: number;
  sharePercentage: number;
}

export type ProductGroupKpi =
  | 'marketRevenue'
  | 'marketVolume'
  | 'yourRevenue'
  | 'yourVolume'
  | 'marketPrice'
  | 'yourPrice';

export enum ColumnHeaderForPA {
  categoryGroup = 'categoryGroup',
  marketRevenue = 'marketRevenue',
  marketVolume = 'marketVolume',
  yourRevenue = 'yourRevenue',
  yourRevenueShare = 'yourRevenueShare',
  yourRevenueMix = 'yourRevenueMix',
  yourVolume = 'yourVolume',
  yourVolumeMix = 'yourVolumeMix',
  yourVolumeShare = 'yourVolumeShare',
  marketPrice = 'marketPrice',
  yourPrice = 'yourPrice',
  marketRetailerRevenueShare = 'marketRetailerRevenueShare',
  marketRetailerVolumeShare = 'marketRetailerVolumeShare',
  marketExcludeRetailRevenue = 'marketExcludeRetailRevenue',
  marketExcludeRetailVolume = 'marketExcludeRetailVolume',
  marketExcludeRetailPrice = 'marketExcludeRetailPrice',
}

export enum SortKeys {
  Absolute = '',
  Change = 'Change',
  Alphabetically = 'Alphabetically',
}

export enum SortLabels {
  Absolute = 'Absolute',
  Change = 'Change',
  Ascending = 'Ascending',
  Descending = 'Descending',
  Alphabetically = 'Alphabetically',
}

export enum ProductGroupKpiPercentage {
  YourRevenueShareMix = 'yourRevenueShareMix',
  YourVolumeShareMix = 'yourVolumeShareMix',
}

export const RevenueShareType = ['yourRevenueShare', 'yourVolumeShare', 'yourRevenueMix', 'yourVolumeMix'];

// KPIs that is required to display in currency for actual growth
export const RevenuePriceType = [
  'marketRevenue',
  'yourRevenue',
  'retailerRevenue',
  'myRevenue',
  'marketPrice',
  'yourPrice',
  'retailerPrice',
  'marketExcludeRetailRevenue',
  'marketExcludeRetailPrice',
];

export const ColumnHeaderDisplayNameForPA = (volume: string = DEFAULT_VOLUME) => ({
  [ColumnHeaderForPA.categoryGroup]: 'Market',
  [ColumnHeaderForPA.marketRevenue]: 'Market Revenue',
  [ColumnHeaderForPA.marketVolume]: `Market ${volume}`,
  [ColumnHeaderForPA.yourRevenue]: 'Retailer Revenue',
  [ColumnHeaderForPA.yourRevenueShare]: 'Retailer Revenue Share',
  [ColumnHeaderForPA.yourRevenueMix]: 'Retailer Revenue Mix',
  [ColumnHeaderForPA.yourVolume]: `Retailer ${volume}`,
  [ColumnHeaderForPA.yourVolumeMix]: `Retailer ${volume} Mix`,
  [ColumnHeaderForPA.yourVolumeShare]: `Retailer ${volume} Share`,
  [ColumnHeaderForPA.marketPrice]: 'Market Price',
  [ColumnHeaderForPA.yourPrice]: 'Retailer Price',
  [ColumnHeaderForPA.marketRetailerRevenueShare]: REVENUE_SHARE_MARKET_VS_RETAILER,
  [ColumnHeaderForPA.marketRetailerVolumeShare]: `${volume} (Market vs Retailer)`,
  [ColumnHeaderForPA.marketExcludeRetailRevenue]: 'Market excl. Retailer Revenue',
  [ColumnHeaderForPA.marketExcludeRetailVolume]: 'Market excl. Retailer Units',
  [ColumnHeaderForPA.marketExcludeRetailPrice]: 'Market excl. Retailer Price',
});

export const getDisplayNameFromHeaderKey = (headerKey: string, volume: string): string => {
  return ColumnHeaderDisplayNameForPA(volume)[headerKey as ColumnHeaderForPA];
};

export const REVENUE_SHARE_MARKET_VS_RETAILER = 'Revenue (Market vs Retailer)';
export const TOTAL_REVENUE = 'Total Revenue';
export const CategoryGroup = 'categoryGroup';

export const REVENUE = 'Revenue';
export const TOTAL = 'Total';

export const PerformanceAnalysisboardTableId = 'performance-analysisboard';

export const DefaultSortingOptionsForPerformanceAnalysisboard: SortingOptionsType = {
  headerKey: ColumnHeaderForPA.yourRevenue,
  sortKey: SortKeys.Absolute,
  sortDir: SortDirection.desc,
  label: SortLabels.Absolute,
};

export type KPIDropdownOption = {
  value: string;
  label: string;
  headerKey: ColumnHeaderForPA;
  salesType: PerformanceType;
};

export const kpiDropdownOptions = (volume: string): KPIDropdownOption[] => [
  {
    value: 'Revenue Share',
    label: 'Revenue (Market vs Retailer)',
    headerKey: ColumnHeaderForPA.marketRetailerRevenueShare,
    salesType: PerformanceType.Value,
  },
  {
    value: 'Volume Share',
    label: `${volume} (Market vs Retailer)`,
    headerKey: ColumnHeaderForPA.marketRetailerVolumeShare,
    salesType: PerformanceType.Volume,
  },
  {
    value: 'Market Revenue',
    label: 'Market Revenue',
    headerKey: ColumnHeaderForPA.marketRevenue,
    salesType: PerformanceType.Value,
  },
  {
    value: 'Market Volume',
    label: `Market ${volume}`,
    headerKey: ColumnHeaderForPA.marketVolume,
    salesType: PerformanceType.Volume,
  },
  {
    value: 'Market Price',
    label: 'Market Price',
    headerKey: ColumnHeaderForPA.marketPrice,
    salesType: PerformanceType.Value,
  },
  {
    value: 'My Revenue',
    label: 'Retailer Revenue',
    headerKey: ColumnHeaderForPA.yourRevenue,
    salesType: PerformanceType.Value,
  },
  {
    value: 'My Revenue Share',
    label: 'Retailer Revenue Share',
    headerKey: ColumnHeaderForPA.yourRevenueShare,
    salesType: PerformanceType.Value,
  },
  {
    value: 'My Volume',
    label: `Retailer ${volume}`,
    headerKey: ColumnHeaderForPA.yourVolume,
    salesType: PerformanceType.Volume,
  },
  {
    value: 'My Volume Share',
    label: `Retailer ${volume} Share`,
    headerKey: ColumnHeaderForPA.yourVolumeShare,
    salesType: PerformanceType.Volume,
  },
  {
    value: 'My Price',
    label: 'Retailer Price',
    headerKey: ColumnHeaderForPA.yourPrice,
    salesType: PerformanceType.Value,
  },
  {
    value: 'Market excl. Retail Revenue',
    label: 'Market excl. Retailer Revenue',
    headerKey: ColumnHeaderForPA.marketExcludeRetailRevenue,
    salesType: PerformanceType.Value,
  },
  {
    value: 'Market excl. Retail Volume',
    label: 'Market excl. Retailer Volume',
    headerKey: ColumnHeaderForPA.marketExcludeRetailVolume,
    salesType: PerformanceType.Volume,
  },
  {
    value: 'Market excl. Retail Price',
    label: 'Market excl. Retailer Price',
    headerKey: ColumnHeaderForPA.marketExcludeRetailPrice,
    salesType: PerformanceType.Value,
  },
];

export const kpiDisplayNameFromHeader = (volume: string) => [
  ...kpiDropdownOptions(volume),
  {
    value: 'My Revenue Mix',
    label: 'Retailer Revenue Mix',
    headerKey: ColumnHeaderForPA.yourRevenueMix,
    salesType: PerformanceType.Value,
  },
  {
    value: 'My Volume Mix',
    label: `Retailer ${volume} Mix`,
    headerKey: ColumnHeaderForPA.yourVolumeMix,
    salesType: PerformanceType.Value,
  },
];

export const DefaultSortByOptionsForPerformanceAnalysisStackedChart: SortFieldDisplayProps = {
  sortKey: SortKeys.Absolute,
  label: SortLabels.Absolute,
  sortDirection: SortDirectionType.desc ?? 'desc',
};

export const stackedChartSortByOptions: SortFieldDisplayProps[] = [
  {
    sortKey: SortKeys.Alphabetically,
    label: SortLabels.Alphabetically,
    sortDirection: SortDirectionType.asc,
  },
  {
    sortKey: SortKeys.Alphabetically,
    label: SortLabels.Alphabetically,
    sortDirection: SortDirectionType.desc ?? 'desc',
  },
  {
    sortKey: SortKeys.Absolute,
    label: SortLabels.Absolute,
    sortDirection: SortDirectionType.asc,
  },
  {
    sortKey: SortKeys.Absolute,
    label: SortLabels.Absolute,
    sortDirection: SortDirectionType.desc ?? 'desc',
  },
];

export interface WarningMessageFeatureValue {
  warningMessage: string;
}

export const getSortDirectionLabel = (sortDirection: SortDirection, sortKey: SortKeys) => {
  return sortDirection === SortDirection.asc ? '(Ascending)' : '(Descending)';
};
